/** Geometry primitives. All values are logical (CSS) pixels unless noted. */

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Insets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How a control sits in the space its parent gives it, on one axis.
 *
 * The same four values WPF uses, and the same ones `LayoutPanel` already arranges its children
 * with - `CrossAlign` is an alias for this. Naming them once means a control aligned inside a
 * `Grid` cell and a control aligned inside a plain `Panel` are saying the same word.
 */
export type Alignment = "stretch" | "start" | "center" | "end";

/**
 * Place one axis by alignment rather than by coordinate.
 *
 * Returns the new offset and extent. `stretch` fills what the margin leaves; the others keep
 * the control's own extent and move it. Pure, so it can be reasoned about and tested without a
 * control tree.
 */
export function alignAxis(
  align: Alignment,
  available: number,
  extent: number,
  before: number,
  after: number,
): { offset: number; extent: number } {
  const room = Math.max(0, available - before - after);
  switch (align) {
    case "stretch":
      return { offset: before, extent: room };
    case "end":
      return { offset: Math.max(before, available - after - extent), extent };
    case "center":
      // Centered in the room the margins leave, which is what makes an asymmetric margin nudge
      // a centered control off center - the WPF behavior, and the useful one.
      return { offset: before + Math.round((room - extent) / 2), extent };
    default:
      return { offset: before, extent };
  }
}

export function pt(x: number, y: number): Point {
  return { x, y };
}

export function rect(x: number, y: number, width: number, height: number): Rect {
  return { x, y, width, height };
}

export function insets(left: number, top = left, right = left, bottom = top): Insets {
  return { left, top, right, bottom };
}

export const EMPTY_RECT: Rect = { x: 0, y: 0, width: 0, height: 0 };

export function rectRight(r: Rect): number {
  return r.x + r.width;
}

export function rectBottom(r: Rect): number {
  return r.y + r.height;
}

export function rectIsEmpty(r: Rect): boolean {
  return r.width <= 0 || r.height <= 0;
}

export function rectContains(r: Rect, p: Point): boolean {
  return p.x >= r.x && p.x < r.x + r.width && p.y >= r.y && p.y < r.y + r.height;
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    rectRight(inner) <= rectRight(outer) &&
    rectBottom(inner) <= rectBottom(outer)
  );
}

export function rectIntersect(a: Rect, b: Rect): Rect {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const r = Math.min(rectRight(a), rectRight(b));
  const bt = Math.min(rectBottom(a), rectBottom(b));
  return { x, y, width: Math.max(0, r - x), height: Math.max(0, bt - y) };
}

export function rectIntersects(a: Rect, b: Rect): boolean {
  return (
    a.x < rectRight(b) && rectRight(a) > b.x && a.y < rectBottom(b) && rectBottom(a) > b.y
  );
}

export function rectUnion(a: Rect, b: Rect): Rect {
  if (rectIsEmpty(a)) return { ...b };
  if (rectIsEmpty(b)) return { ...a };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(rectRight(a), rectRight(b)) - x,
    height: Math.max(rectBottom(a), rectBottom(b)) - y,
  };
}

/** Grow (positive) or shrink (negative) a rect on all sides. */
export function rectInflate(r: Rect, dx: number, dy = dx): Rect {
  return { x: r.x - dx, y: r.y - dy, width: r.width + dx * 2, height: r.height + dy * 2 };
}

/** Shrink a rect by per-side insets (e.g. padding or a border). */
export function rectDeflate(r: Rect, i: Insets): Rect {
  return {
    x: r.x + i.left,
    y: r.y + i.top,
    width: Math.max(0, r.width - i.left - i.right),
    height: Math.max(0, r.height - i.top - i.bottom),
  };
}

export function rectOffset(r: Rect, dx: number, dy: number): Rect {
  return { x: r.x + dx, y: r.y + dy, width: r.width, height: r.height };
}

export function rectEquals(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

/** Center `inner` (a size) inside `outer`, rounded to whole pixels. */
export function rectCenter(outer: Rect, inner: Size): Rect {
  return {
    x: Math.round(outer.x + (outer.width - inner.width) / 2),
    y: Math.round(outer.y + (outer.height - inner.height) / 2),
    width: inner.width,
    height: inner.height,
  };
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Distance between two points. */
export function distance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Shrink and move `r` so the whole of it sits inside `area`.
 *
 * `min` is honored only while it fits: past that the area wins, because a
 * window too small to be comfortable still beats one that cannot be reached.
 */
export function fitRectInto(r: Rect, area: Rect, min: Size): Rect {
  const width = Math.min(Math.max(r.width, Math.min(min.width, area.width)), area.width);
  const height = Math.min(Math.max(r.height, Math.min(min.height, area.height)), area.height);
  return {
    x: clamp(r.x, area.x, Math.max(area.x, area.x + area.width - width)),
    y: clamp(r.y, area.y, Math.max(area.y, area.y + area.height - height)),
    width,
    height,
  };
}

/**
 * Move `r` the least distance that leaves it grabbable inside `area`.
 *
 * Weaker than `fitRectInto` on purpose: a window deliberately pushed half off
 * the edge stays where it was put, as long as `edge` pixels of it and the top
 * `grip` pixels remain on screen. Used when the screen changes size under a
 * layout, not when placing something new.
 */
export function nudgeRectIntoReach(r: Rect, area: Rect, edge: number, grip: number): Point {
  return {
    x: clamp(r.x, area.x + edge - r.width, area.x + area.width - edge),
    y: clamp(r.y, area.y, area.y + Math.max(0, area.height - grip)),
  };
}

/**
 * Move `r` so that it lies inside `area`, without resizing it.
 *
 * Where `fitRectInto` shrinks something too big and pins it to the top-left,
 * this lets it slide: the clamp range reverses, so any part of an oversized
 * rectangle can still be brought into view. That is the difference between
 * placing a window and dragging one — a drag must never resize what it is
 * moving, and must never strand a far edge out of reach.
 */
export function slideRectInto(r: Rect, area: Rect): Point {
  const restX = area.x + area.width - r.width;
  const restY = area.y + area.height - r.height;
  return {
    x: clamp(r.x, Math.min(area.x, restX), Math.max(area.x, restX)),
    y: clamp(r.y, Math.min(area.y, restY), Math.max(area.y, restY)),
  };
}
